<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="720px"
    scrollable
    hide-overlay
  >
    <v-card
      class="d-flex flex-column"
      data-cy="privacy-consent-dialog"
    >
      <v-card-title class="text-h6">
        Tratamiento de datos personales
      </v-card-title>

      <v-card-text
        class="privacy-consent-scroll"
        style="max-height: 60vh;"
      >
        <div
          v-if="loadError"
          class="py-4"
        >
          <p class="body-2 error--text mb-4">
            {{ loadError }}
          </p>
          <v-btn
            color="primary"
            outlined
            @click="$emit('retry')"
          >
            Reintentar
          </v-btn>
        </div>

        <div
          v-else-if="!hasConsentContent"
          class="d-flex justify-center py-8"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </div>

        <template v-else>
          <p class="body-2 mb-4">
            {{ consent.acceptance_text }}
          </p>

          <div
            v-for="(section, sectionIndex) in consent.info_sections"
            :key="sectionIndex"
            class="mb-4"
          >
            <p class="subtitle-2 font-weight-bold mb-2">
              {{ section.title }}
            </p>
            <div
              v-for="(item, itemIndex) in section.items"
              :key="itemIndex"
              class="mb-2"
            >
              <span class="font-weight-medium">{{ item.label }}:</span>
              {{ item.value }}
            </div>
          </div>

          <p class="body-2">
            {{ consent.additional_info_text }}
            <a
              :href="policyUrl"
              target="_blank"
              rel="noopener noreferrer"
            >Política de privacidad</a>.
          </p>

          <v-alert
            v-if="acceptError"
            type="error"
            dense
            class="mb-2"
          >
            {{ acceptError }}
          </v-alert>

          <v-checkbox
            v-model="hasAcceptedCheckbox"
            class="mt-2"
            hide-details
            :label="consent.checkbox_label"
            data-cy="privacy-consent-checkbox"
          />
        </template>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="!hasAcceptedCheckbox || isSubmitting || !hasConsentContent || !!loadError"
          :loading="isSubmitting"
          data-cy="privacy-consent-accept"
          @click="submitAcceptance"
        >
          Aceptar
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-overlay
      color="gray"
      z-index="-1"
      :value="true"
      style="background: rgba(0, 0, 0, 0.04); backdrop-filter: blur(10px);"
    />
  </v-dialog>
</template>

<script>
import BackendApi from '@/services/backend.service'

export default {
  name: 'PrivacyConsentDialog',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    consent: {
      type: Object,
      default: null,
    },
    policyUrl: {
      type: String,
      default: '',
    },
    loadError: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      hasAcceptedCheckbox: false,
      isSubmitting: false,
      acceptError: null,
    }
  },
  computed: {
    dialog: {
      get() {
        return this.isOpen
      },
      set(newVal) {
        this.$emit('update:is-open', newVal)
      },
    },
    hasConsentContent() {
      return Boolean(
        this.consent?.acceptance_text &&
        Array.isArray(this.consent?.info_sections) &&
        this.consent.info_sections.length > 0
      )
    },
  },
  watch: {
    isOpen(open) {
      if (!open) {
        this.hasAcceptedCheckbox = false
        this.acceptError = null
      }
    },
  },
  methods: {
    async submitAcceptance() {
      if (!this.hasAcceptedCheckbox || this.isSubmitting || !this.hasConsentContent) {
        return
      }

      this.isSubmitting = true
      this.acceptError = null

      try {
        const response = await BackendApi.post('/audit/privacy-policy/accept')
        if (!response?.data?.success) {
          this.acceptError =
            response?.data?.message || 'No se pudo registrar la aceptación'
          return
        }
        $cookies.set('needs_privacy_consent', false)
        this.$emit('accepted')
        this.$emit('update:is-open', false)
      } catch (error) {
        this.acceptError =
          error?.response?.data?.message ||
          error?.message ||
          'No se pudo registrar la aceptación. Intente de nuevo.'
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>
